import {
  type AttachmentPreviewKind,
  type AttachmentUploadRefusalReason,
  type AttachmentUploadTicket,
  type ChatFileAttachment,
  type ChatFileAttachmentOrigin,
  classifyAttachmentPreview,
} from "@cleo/contracts";
import type { EnvironmentId, ThreadId } from "@cleo/contracts";
import * as Schema from "effect/Schema";
import { create } from "zustand";

// UP8 — the composer's file attachments (anything that is not an inline
// image). A file uploads the moment it is attached; the composer holds the
// upload's state per draft target so a draft move, a stash or a reload keeps
// what finished and marks what did not as "Attach again". An attachment is a
// boundary member of the message that carries it: nothing here is attached
// by default, and Send carries exactly the finished ones.
//
// The orchestration contract's `ChatAttachment` union carries a stored-file
// member (0b678df7) and the Desk's Send carries the finished uploads by
// reference. The gate stays as the one place a build that cannot carry files
// refuses Send explicitly instead of dropping them on the floor.
export const COMPOSER_FILE_ATTACHMENTS_SEND_WIRED = true;

export const COMPOSER_FILE_ATTACHMENTS_STORAGE_KEY = "cleo:composer-file-attachments:v1";
export const MAX_COMPOSER_FILE_ATTACHMENTS = 8;

/**
 * File attachments are keyed by the thread they upload for. A local draft
 * keeps its ThreadId when it is promoted to a server thread, so the chips
 * survive that move without a re-key; a draft that changes project gets a new
 * thread and moves its attachments with `moveTarget`.
 */
export function composerFileAttachmentsTargetKey(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): string {
  return `${environmentId}:${threadId}`;
}

export type ComposerFileAttachmentStatus =
  | { readonly phase: "uploading"; readonly sentBytes: number; readonly totalBytes: number }
  | {
      readonly phase: "uploaded";
      readonly attachment: ChatFileAttachment;
      readonly ticket: AttachmentUploadTicket;
    }
  | {
      readonly phase: "refused";
      readonly reason: AttachmentUploadRefusalReason | "no-upload-door" | "relay-proof" | "failed";
      readonly detail: string;
      /** True when the bytes are still on this device and the person can attach again. */
      readonly retryable: boolean;
    };

export interface ComposerFileAttachment {
  readonly localId: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly origin: ChatFileAttachmentOrigin;
  readonly previewKind: AttachmentPreviewKind;
  /** An object URL for an image or video still on this device; null after a reload. */
  readonly previewUrl: string | null;
  /** The bytes, while this device still holds them. */
  readonly file: File | null;
  readonly status: ComposerFileAttachmentStatus;
}

const PersistedUploadedFileAttachment = Schema.Struct({
  localId: Schema.String,
  name: Schema.String,
  mimeType: Schema.String,
  sizeBytes: Schema.Number,
  origin: Schema.Literals(["picker", "paste", "folded-paste", "drop"]),
  attachment: Schema.optional(Schema.Unknown),
  ticket: Schema.optional(Schema.Unknown),
});
const PersistedFileAttachments = Schema.Record(
  Schema.String,
  Schema.Array(PersistedUploadedFileAttachment),
);
const decodePersisted = Schema.decodeUnknownOption(PersistedFileAttachments);

function storage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

let memoryPersisted: Record<
  string,
  ReadonlyArray<typeof PersistedUploadedFileAttachment.Type>
> = {};

function readPersisted(): Record<
  string,
  ReadonlyArray<typeof PersistedUploadedFileAttachment.Type>
> {
  const store = storage();
  if (store === null) return memoryPersisted;
  try {
    const raw = store.getItem(COMPOSER_FILE_ATTACHMENTS_STORAGE_KEY);
    if (!raw) return {};
    const decoded = decodePersisted(JSON.parse(raw));
    return decoded._tag === "Some" ? decoded.value : {};
  } catch {
    return {};
  }
}

function writePersisted(
  byTarget: Record<string, ReadonlyArray<ComposerFileAttachment>>,
  requireDurable = false,
): void {
  const record: Record<string, Array<typeof PersistedUploadedFileAttachment.Type>> = {};
  for (const [target, attachments] of Object.entries(byTarget)) {
    const entries = attachments.map((attachment) => ({
      localId: attachment.localId,
      name: attachment.name,
      mimeType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      origin: attachment.origin,
      ...(attachment.status.phase === "uploaded"
        ? { attachment: attachment.status.attachment, ticket: attachment.status.ticket }
        : {}),
    }));
    record[target] = entries; // Empty is a deliberate removal, distinct from never hydrated.
  }
  const store = storage();
  if (store === null) {
    if (requireDurable) throw new Error("Attachment storage is unavailable");
    memoryPersisted = record;
    return;
  }
  try {
    const value = Object.keys(record).length === 0 ? null : JSON.stringify(record);
    if (value === null) store.removeItem(COMPOSER_FILE_ATTACHMENTS_STORAGE_KEY);
    else store.setItem(COMPOSER_FILE_ATTACHMENTS_STORAGE_KEY, value);
    if (requireDurable && store.getItem(COMPOSER_FILE_ATTACHMENTS_STORAGE_KEY) !== value)
      throw new Error("Attachment retirement could not be confirmed");
  } catch (error) {
    if (requireDurable) throw error;
    // A blocked storage keeps the previous record; the live draft is unaffected.
  }
}

export function persistComposerFileAttachmentsNow(): void {
  writePersisted(useComposerFileAttachmentsStore.getState().byTarget, true);
}

/** What a reload restores: finished uploads as themselves, unfinished ones as "Attach again" markers. */
export function hydrateComposerFileAttachments(): Record<string, ComposerFileAttachment[]> {
  const persisted = readPersisted();
  const byTarget: Record<string, ComposerFileAttachment[]> = {};
  for (const [target, entries] of Object.entries(persisted)) {
    const attachments = entries.map((entry): ComposerFileAttachment => {
      const base = {
        localId: entry.localId,
        name: entry.name,
        mimeType: entry.mimeType,
        sizeBytes: entry.sizeBytes,
        origin: entry.origin,
        previewKind: classifyAttachmentPreview({ mimeType: entry.mimeType, name: entry.name }),
        previewUrl: null,
        file: null,
      };
      const uploaded = decodeUploaded(entry.attachment, entry.ticket);
      return uploaded === null
        ? {
            ...base,
            status: {
              phase: "refused",
              reason: "interrupted",
              detail: "This upload did not finish before the page closed.",
              retryable: false,
            },
          }
        : { ...base, status: { phase: "uploaded", ...uploaded } };
    });
    byTarget[target] = attachments;
  }
  return byTarget;
}

const UploadedPair = Schema.Struct({
  attachment: Schema.Struct({
    type: Schema.Literal("file"),
    id: Schema.String,
    name: Schema.String,
    mimeType: Schema.String,
    sizeBytes: Schema.Number,
    contentSha256: Schema.optional(Schema.String),
    origin: Schema.optional(Schema.Literals(["picker", "paste", "folded-paste", "drop"])),
  }),
  ticket: Schema.Struct({
    attachmentId: Schema.String,
    uploadUrl: Schema.String,
    expiresAt: Schema.Number,
    maxBytes: Schema.Number,
  }),
});
const decodeUploadedPair = Schema.decodeUnknownOption(UploadedPair);

function decodeUploaded(
  attachment: unknown,
  ticket: unknown,
): { attachment: ChatFileAttachment; ticket: AttachmentUploadTicket } | null {
  if (attachment === undefined || ticket === undefined) return null;
  const decoded = decodeUploadedPair({ attachment, ticket });
  return decoded._tag === "Some"
    ? {
        attachment: decoded.value.attachment as ChatFileAttachment,
        ticket: decoded.value.ticket as AttachmentUploadTicket,
      }
    : null;
}

export interface ComposerFileAttachmentsState {
  readonly byTarget: Record<string, ComposerFileAttachment[]>;
  readonly list: (target: string) => ReadonlyArray<ComposerFileAttachment>;
  readonly add: (target: string, attachment: ComposerFileAttachment) => void;
  readonly setStatus: (
    target: string,
    localId: string,
    status: ComposerFileAttachmentStatus,
  ) => void;
  readonly remove: (target: string, localId: string) => ComposerFileAttachment | null;
  readonly take: (target: string) => ComposerFileAttachment[];
  readonly moveTarget: (from: string, to: string) => void;
  readonly replaceAll: (byTarget: Record<string, ComposerFileAttachment[]>) => void;
}

const EMPTY: ReadonlyArray<ComposerFileAttachment> = [];

export const useComposerFileAttachmentsStore = create<ComposerFileAttachmentsState>()(
  (set, get) => ({
    byTarget: {},
    list: (target) => get().byTarget[target] ?? EMPTY,
    add: (target, attachment) => {
      set((state) => {
        const byTarget = {
          ...state.byTarget,
          [target]: [...(state.byTarget[target] ?? []), attachment],
        };
        writePersisted(byTarget);
        return { byTarget };
      });
    },
    setStatus: (target, localId, status) => {
      set((state) => {
        const current = state.byTarget[target];
        if (!current?.some((attachment) => attachment.localId === localId)) return state;
        const byTarget = {
          ...state.byTarget,
          [target]: current.map((attachment) =>
            attachment.localId === localId ? { ...attachment, status } : attachment,
          ),
        };
        writePersisted(byTarget);
        return { byTarget };
      });
    },
    remove: (target, localId) => {
      const removed = get().byTarget[target]?.find((a) => a.localId === localId) ?? null;
      if (removed === null) return null;
      set((state) => {
        const remaining = (state.byTarget[target] ?? []).filter((a) => a.localId !== localId);
        const byTarget = { ...state.byTarget };
        byTarget[target] = remaining;
        writePersisted(byTarget);
        return { byTarget };
      });
      return removed;
    },
    take: (target) => {
      const taken = get().byTarget[target] ?? [];
      if (taken.length === 0) return [];
      set((state) => {
        const byTarget = { ...state.byTarget };
        byTarget[target] = [];
        writePersisted(byTarget);
        return { byTarget };
      });
      return [...taken];
    },
    moveTarget: (from, to) => {
      if (from === to) return;
      set((state) => {
        const moving = state.byTarget[from];
        if (!moving || moving.length === 0) return state;
        const byTarget = { ...state.byTarget };
        byTarget[from] = [];
        byTarget[to] = [...(byTarget[to] ?? []), ...moving];
        writePersisted(byTarget);
        return { byTarget };
      });
    },
    replaceAll: (byTarget) => {
      writePersisted(byTarget);
      set(() => ({ byTarget }));
    },
  }),
);

/** The finished attachments a Send carries for a target, in the order they were attached. */
export function uploadedFileAttachments(
  attachments: ReadonlyArray<ComposerFileAttachment>,
): ChatFileAttachment[] {
  return attachments.flatMap((attachment) =>
    attachment.status.phase === "uploaded" ? [attachment.status.attachment] : [],
  );
}

export function hasFileUploadsInFlight(
  attachments: ReadonlyArray<ComposerFileAttachment>,
): boolean {
  return attachments.some((attachment) => attachment.status.phase === "uploading");
}

/**
 * Said when a Send would go to Cleo mid-response on an engine whose steer
 * carries text only (`midRunAttachments` false): the attachments stay.
 */
export const MID_RUN_TEXT_ONLY_REFUSAL = {
  title: "Cleo can't take attachments while she's working on this engine.",
  description: "They stay in the composer. Send them when this response finishes.",
} as const;

/** Why Send cannot go yet, or null. Explicit: an unfinished or refused attachment is never dropped silently. */
export function fileAttachmentsSendRefusal(
  attachments: ReadonlyArray<ComposerFileAttachment>,
  options: {
    readonly sendWired: boolean;
    readonly queueing: boolean;
    /** This Send goes to Cleo mid-response and that delivery carries text only. */
    readonly midRunTextOnly?: boolean;
  },
): { readonly title: string; readonly description: string } | null {
  if (attachments.length === 0) return null;
  if (options.midRunTextOnly) return MID_RUN_TEXT_ONLY_REFUSAL;
  if (options.queueing) {
    return {
      title: "Queued messages currently accept text.",
      description:
        "Keep the attached files in the composer and send them when this response finishes.",
    };
  }
  if (hasFileUploadsInFlight(attachments)) {
    return {
      title: "Still uploading an attachment.",
      description: "Send again once every attachment shows as uploaded.",
    };
  }
  const refused = attachments.find((attachment) => attachment.status.phase === "refused");
  if (refused) {
    return {
      title: `'${refused.name}' is not attached.`,
      description: "Attach it again or remove it before sending.",
    };
  }
  if (!options.sendWired) {
    return {
      title: "File attachments cannot be sent on this build yet.",
      description:
        "The message contract does not carry files here; remove them or wait for the Desk update.",
    };
  }
  return null;
}

export function describeFileAttachmentStatus(attachment: ComposerFileAttachment): string {
  switch (attachment.status.phase) {
    case "uploading": {
      const { sentBytes, totalBytes } = attachment.status;
      const percent =
        totalBytes > 0 ? Math.min(100, Math.floor((sentBytes / totalBytes) * 100)) : 0;
      return `Uploading ${percent}%`;
    }
    case "uploaded":
      return "Uploaded";
    case "refused":
      return attachment.status.retryable ? "Upload failed. Attach again" : "Attach again";
  }
}
